import React, { useEffect, useRef, useState } from 'react';
import { Music2, Pause } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const TRACK = 'https://soundcloud.com/lajojazz/im-gonna-live-till-i-die';

const MusicPlayer = () => {
  const { lang } = useLanguage();
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const gestureEvents = ['click', 'keydown', 'touchstart', 'scroll'];

    const startOnGesture = () => {
      if (widgetRef.current) widgetRef.current.play();
    };
    const removeGesture = () => gestureEvents.forEach((e) => window.removeEventListener(e, startOnGesture));

    const init = () => {
      if (!window.SC || !iframeRef.current || widgetRef.current) return;
      const w = window.SC.Widget(iframeRef.current);
      widgetRef.current = w;
      w.bind(window.SC.Widget.Events.READY, () => {
        w.setVolume(55);
        w.play(); // attempt immediate autoplay (may be blocked until a gesture)
      });
      w.bind(window.SC.Widget.Events.PLAY, () => {
        setPlaying(true);
        removeGesture();
      });
      w.bind(window.SC.Widget.Events.PAUSE, () => setPlaying(false));
      w.bind(window.SC.Widget.Events.FINISH, () => setPlaying(false));
    };

    // Register gesture fallback first
    gestureEvents.forEach((e) => window.addEventListener(e, startOnGesture, { passive: true }));

    // Load SoundCloud Widget API
    const id = 'sc-widget-api';
    if (window.SC) {
      init();
    } else {
      let s = document.getElementById(id);
      if (!s) {
        s = document.createElement('script');
        s.id = id;
        s.src = 'https://w.soundcloud.com/player/api.js';
        s.onload = init;
        document.body.appendChild(s);
      } else {
        s.addEventListener('load', init);
      }
    }

    return removeGesture;
  }, []);

  const toggle = () => {
    const w = widgetRef.current;
    if (!w) return;
    w.isPaused((paused) => (paused ? w.play() : w.pause()));
  };

  const label = lang === 'es' ? 'Música' : 'Music';

  return (
    <>
      <iframe
        ref={iframeRef}
        title="LAJO Track"
        allow="autoplay"
        style={{ position: 'absolute', width: 1, height: 1, left: -9999, top: -9999, opacity: 0, pointerEvents: 'none' }}
        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(TRACK)}&auto_play=true&visual=false&hide_related=true&show_comments=false&show_user=false&show_teaser=false`}
      />

      <button
        onClick={toggle}
        aria-label={label}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-[#14120d]/90 backdrop-blur-md border border-[#e6b83f]/40 text-[#f5deA0] shadow-lg transition-all hover:border-[#e6b83f]/80 hover:scale-[1.03]"
      >
        <span className="relative w-9 h-9 rounded-full bg-[#e6b83f] flex items-center justify-center text-[#14120d]">
          {playing ? (
            <span className="flex items-end gap-[2px] h-4">
              <span className="eq-bar" style={{ animationDelay: '0ms' }} />
              <span className="eq-bar" style={{ animationDelay: '150ms' }} />
              <span className="eq-bar" style={{ animationDelay: '300ms' }} />
            </span>
          ) : (
            <Music2 size={17} />
          )}
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="font-sans-el text-[11px] tracking-[0.15em] uppercase text-[#c2bbaa]">{playing ? 'LAJO' : label}</span>
          <span className="font-sans-el text-[12px] text-[#f5deA0] flex items-center gap-1">
            {playing ? <><Pause size={11} /> {lang === 'es' ? 'Pausar' : 'Pause'}</> : "I'm Gonna Live Till I Die"}
          </span>
        </span>
      </button>
    </>
  );
};

export default MusicPlayer;
